import React from 'react';
import { Box, Tooltip, Typography } from '@mui/material';

import useTranslate from 'hooks/useTranslate';
import type { DailyMetric } from 'hooks/analytics';

import { AnalyticsColors } from '../../../components/analyticsUi';

interface ProviderActivityHeatmapProps {
    daily: DailyMetric[];
    compact?: boolean;
}

const getTodayKey = (): string => new Date().toISOString().slice(0, 10);

const formatDay = (day: string): string =>
    new Intl.DateTimeFormat(undefined, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
    }).format(new Date(`${day}T00:00:00`));

const getCellColor = (value: number, maxValue: number): string => {
    if (value <= 0 || maxValue <= 0) return 'rgba(99, 102, 241, 0.06)';

    const intensity = value / maxValue;
    return `rgba(99, 102, 241, ${0.16 + intensity * 0.7})`;
};

const ProviderActivityHeatmap: React.FC<ProviderActivityHeatmapProps> = ({
    daily,
    compact = false,
}) => {
    const { translate } = useTranslate();
    const maxActions = Math.max(...daily.map((metric) => metric.recordedActions), 0);
    const todayKey = getTodayKey();

    if (daily.length === 0) {
        return (
            <Typography sx={{ color: AnalyticsColors.textTertiary, fontSize: 12 }}>
                {translate('noDailyActivity')}
            </Typography>
        );
    }

    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: `repeat(${daily.length}, minmax(${compact ? 5 : 8}px, 1fr))`,
                gap: compact ? '2px' : '3px',
                minWidth: compact ? 240 : 500,
                width: '100%',
            }}
        >
            {daily.map((metric) => {
                const isToday = metric.day === todayKey;
                const title = `${formatDay(metric.day)}: ${metric.recordedActions.toLocaleString()} ${translate(
                    'recordedActions'
                )}${isToday ? ` · ${translate('livePartialDay')}` : ''}`;

                return (
                    <Tooltip key={metric.day} title={title} arrow>
                        <Box
                            component="span"
                            aria-label={title}
                            sx={{
                                display: 'block',
                                aspectRatio: '1',
                                minWidth: compact ? 5 : 8,
                                borderRadius: '3px',
                                backgroundColor: getCellColor(metric.recordedActions, maxActions),
                                border: isToday
                                    ? `2px solid ${AnalyticsColors.primary}`
                                    : '1px solid transparent',
                                boxSizing: 'border-box',
                            }}
                        />
                    </Tooltip>
                );
            })}
        </Box>
    );
};

export default ProviderActivityHeatmap;
